import { BALANCE_SHEET_POSITIONS } from '../constants/account-positions.constant';
import { FUNCTION_PROOFS, functionsOfPosition } from '../constants/account-functions.constant';
import { AccountsStatusCounts } from '../components/accounts-overview/accounts-overview.types';
import { IProjectAccount } from '../models/project-account.model';
import { amountOf } from './balance-sheet.presenter';
import { groupByPosition, statusOf } from './accounts-presentation';
import { accountsWithoutPosition } from './chart-of-accounts.presenter';

/**
 * REV-22476 (Part 3) — the Jahresabschluss overview: what is to do in this closing, step by step.
 *
 * Three steps are real today and computed here from what the closing already knows — the account list
 * and the proof table (`FUNCTION_PROOFS`, a copy of the kernel's). The others are "coming soon" and carry no
 * data, so they are not computed at all.
 *
 * The list is the DEFAULT view (`stepAccounts`): accounts without a balance are not in it, so they need
 * no position, no proof and no reconciliation here.
 *
 *   done     green — nothing left in the step
 *   open     orange — something left to do
 *   none     grey — no rule for "done" yet, or nothing to show
 */
export type OverviewStepState = 'done' | 'open' | 'none';

export interface OverviewAccountRef {
    readonly accountNumber: string;
    readonly name: string;
}

/** Step 1 — the Kontenplan. */
export interface OverviewChartOfAccounts {
    readonly state: OverviewStepState;
    /** Accounts without a position — what keeps the closing workspace locked. */
    readonly withoutPosition: number;
    /** Accounts under a position that carries functions, without one ("Funktion unbekannt"). */
    readonly withoutFunction: readonly OverviewAccountRef[];
}

/** Step 3 — one row per account function whose proof is missing on at least one account. */
export interface OverviewMissingProof {
    readonly accountFunction: string;
    /** The labels of every document the function accepts as proof. */
    readonly proofLabels: readonly string[];
    readonly accounts: readonly OverviewAccountRef[];
}

export interface OverviewDocuments {
    readonly state: OverviewStepState;
    readonly missing: readonly OverviewMissingProof[];
    /** The Kontenplan is not complete yet: which accounts need a proof is not known. */
    readonly waitingForChartOfAccounts: boolean;
}

/** Step 4 — one row per balance-sheet position. */
export interface OverviewPosition {
    readonly position: string;
    readonly label: string;
    /** In units and in the statement's display sign, like the Konten page shows it. */
    readonly balance: number;
    readonly reconciled: number;
    readonly total: number;
    /** Its accounts per status, for the same pie the Konten table shows. */
    readonly statusCounts: AccountsStatusCounts;
}

export interface OverviewBalanceSheet {
    readonly state: OverviewStepState;
    readonly positions: readonly OverviewPosition[];
    /** The Kontenplan is not complete yet: positions without their accounts are not done. */
    readonly waitingForChartOfAccounts: boolean;
}

export interface ClosingOverview {
    readonly chartOfAccounts: OverviewChartOfAccounts;
    readonly documents: OverviewDocuments;
    readonly balanceSheet: OverviewBalanceSheet;
    /** REV-22527 — the setup's import still runs: no step has a status yet. */
    readonly importing: boolean;
}

const NOTHING = 0;
/** A document is assigned and its reading has not finished — not missing, just not read yet. */
const DOCUMENT_BEING_READ = 'DOCUMENT_BEING_READ';

function refOf(account: IProjectAccount): OverviewAccountRef {
    return { accountNumber: account.accountNumber, name: account.name };
}

function chartOfAccountsOf(accounts: readonly IProjectAccount[]): OverviewChartOfAccounts {
    const withoutPosition = accountsWithoutPosition(accounts).length;
    const withoutFunction = accounts
        .filter(
            (account) => functionsOfPosition(account.accountGroup).length > NOTHING && account.accountFunction === null,
        )
        .map(refOf);

    return {
        state: withoutPosition === NOTHING ? 'done' : 'open',
        withoutPosition,
        withoutFunction,
    };
}

/**
 * Whether an account still lacks its proof: no document has been read for it (`evidenceComplete`),
 * none is being read, and it is not reconciled by hand. Not "a run said so" — before the first run
 * there is no reason on any account, and that must not read as "nothing missing".
 */
function lacksProof(account: IProjectAccount): boolean {
    return (
        !account.evidenceComplete &&
        account.reconciliation !== 'MATCHED' &&
        account.reason?.code !== DOCUMENT_BEING_READ
    );
}

/**
 * The accounts without their proof, grouped by their function — so a row can name every document the
 * function accepts, not only one. Only functions that need a proof (`FUNCTION_PROOFS`), in its order.
 */
function documentsOf(accounts: readonly IProjectAccount[]): Omit<OverviewDocuments, 'waitingForChartOfAccounts'> {
    const missing: OverviewMissingProof[] = [];
    for (const [accountFunction, proofLabels] of Object.entries(FUNCTION_PROOFS)) {
        const without = accounts.filter(
            (account) => account.accountFunction === accountFunction && lacksProof(account),
        );
        if (without.length === NOTHING) {
            continue;
        }
        missing.push({
            accountFunction,
            proofLabels,
            accounts: without.map(refOf),
        });
    }

    return { state: missing.length === NOTHING ? 'done' : 'open', missing };
}

function balanceSheetOf(
    accounts: readonly IProjectAccount[],
    positionLabel: (position: string) => string,
): Omit<OverviewBalanceSheet, 'waitingForChartOfAccounts'> {
    const positions = groupByPosition(accounts, 'BALANCE', BALANCE_SHEET_POSITIONS)
        .filter((group): group is { position: string; accounts: IProjectAccount[] } => group.position !== null)
        .map(({ position, accounts: inPosition }) => ({
            position,
            label: positionLabel(position),
            balance: inPosition.reduce(
                (sum, account) => sum + amountOf(account.saldoInBaseCurrency.amount, position, account.accountNumber),
                NOTHING,
            ),
            reconciled: inPosition.filter((account) => account.reconciliation === 'MATCHED').length,
            total: inPosition.length,
            statusCounts: statusCountsOf(inPosition),
        }));
    const open = positions.some((position) => position.reconciled < position.total);

    return { state: positions.length === NOTHING ? 'none' : open ? 'open' : 'done', positions };
}

function statusCountsOf(accounts: readonly IProjectAccount[]): AccountsStatusCounts {
    const counts: AccountsStatusCounts = { reconciled: 0, difference: 0, open: 0 };
    for (const account of accounts) {
        counts[statusOf(account.reconciliation)] += 1;
    }

    return counts;
}

export function presentClosingOverview(input: {
    readonly accounts: readonly IProjectAccount[];
    readonly positionLabel: (position: string) => string;
    /**
     * REV-22527 — the setup's import still runs. Every step is then `none`: the accounts it would be
     * measured against are not loaded yet, and an empty list reads as «nothing missing». The ONE place
     * this rule lives, so the navigation and the overview cannot disagree about it.
     */
    readonly importing?: boolean;
}): ClosingOverview {
    if (input.importing === true) {
        return importingOverviewOf(input.accounts, input.positionLabel);
    }
    const chartOfAccounts = chartOfAccountsOf(input.accounts);
    // Steps 3 and 4 read positions and functions, so before the Kontenplan is complete they cannot be
    // done: an account without a function needs no proof yet, and «nothing missing» would be a guess.
    const waiting = chartOfAccounts.state === 'open';
    const documents = documentsOf(input.accounts);
    const balanceSheet = balanceSheetOf(input.accounts, input.positionLabel);

    return {
        chartOfAccounts,
        documents: { ...documents, state: waiting ? 'open' : documents.state, waitingForChartOfAccounts: waiting },
        balanceSheet: {
            ...balanceSheet,
            state: waiting && balanceSheet.state === 'done' ? 'open' : balanceSheet.state,
            waitingForChartOfAccounts: waiting,
        },
        importing: false,
    };
}

/** While the import runs: the same shape, every state `none`. */
function importingOverviewOf(
    accounts: readonly IProjectAccount[],
    positionLabel: (position: string) => string,
): ClosingOverview {
    const balanceSheet = balanceSheetOf(accounts, positionLabel);

    return {
        chartOfAccounts: { ...chartOfAccountsOf(accounts), state: 'none' },
        documents: { ...documentsOf(accounts), state: 'none', waitingForChartOfAccounts: false },
        balanceSheet: { ...balanceSheet, state: 'none', waitingForChartOfAccounts: false },
        importing: true,
    };
}
